import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { X, Calendar, BookOpen, Filter, Download, FileSpreadsheet, Printer, FileType, GraduationCap, TrendingUp, Award, User } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { getApiUrl } from "../config/api";
import { TERM_OPTIONS, ASSESSMENT_TYPES } from "../constants/assessments";
import "../styles/teachers.css";
import "../styles/StudentPerformanceModal.css"; // added

interface Student {
  id: string;
  name: string;
  email: string;
  className?: string;
  classId?: string;
}

interface AttendanceRecord {
  id: string;
  date: string;
  status: string;
  className?: string;
}

interface TestResult {
  id: string;
  score: number;
  // nested shape (some endpoints)
  test?: {
      id?: string;
      title?: string;
      term?: string;
      date?: string;
      subject?: {
        name?: string;
      };
    };
  // flattened alternatives (other endpoints)
  testTitle?: string;
  testDate?: string;
  subjectName?: string;
  examType?: string;
  year?: number;
  createdAt?: string;
}

interface StudentPerformanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student;
}

const StudentPerformanceModal: React.FC<StudentPerformanceModalProps> = ({
  isOpen,
  onClose,
  student,
}) => {
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"attendance" | "results">("attendance");

  // Filters
  const [selectedYear, setSelectedYear] = useState<string>("all");
  const [selectedAssessmentType, setSelectedAssessmentType] = useState<string>("all");
  const [selectedTerm, setSelectedTerm] = useState<string>("all");

  // Generate years (current year and past 5 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 6 }, (_, i) => currentYear - i);
  const assessmentTypes = ASSESSMENT_TYPES;

  const token = localStorage.getItem("token");

  // Helper function to get term from date (must be defined before useMemo hooks)
  const getTermFromDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1; // 1-12
    const year = date.getFullYear();

    // Determine term based on month
    // Term One: Jan-Apr, Term Two: May-Aug, Term Three: Sep-Dec
    let termLabel: string;
    if (month >= 9) {
      termLabel = "Term Three";
    } else if (month >= 5) {
      termLabel = "Term Two";
    } else {
      termLabel = "Term One";
    }

    return { term: termLabel, academicYear: year, year };
  };

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        getApiUrl(`attendance?studentId=${student.id}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAttendance(res.data.records || []);
    } catch (err) {
      console.error("Error fetching attendance:", err);
      setAttendance([]);
    } finally {
      setLoading(false);
    }
  }, [student.id, token]);

  const fetchTestResults = useCallback(async () => {
    try {
      const res = await axios.get(
        getApiUrl(`assessments/results/student/${student.id}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResults(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching test results:", err);
      setTestResults([]);
    }
  }, [student.id, token]);

  useEffect(() => {
    if (isOpen && student) {
      fetchAttendance();
      fetchTestResults();
    }
  }, [isOpen, student.id, fetchAttendance, fetchTestResults]);

  // Filter attendance by year
  const filteredAttendance = useMemo(() => {
    if (selectedYear === "all") return attendance;
    return attendance.filter((record) => {
      const recordYear = new Date(record.date).getFullYear();
      return recordYear.toString() === selectedYear;
    });
  }, [attendance, selectedYear]);

  // Filter test results by year and term
  const filteredTestResults = useMemo(() => {
    let filtered = testResults;

    // Filter by year
    if (selectedYear !== "all") {
      filtered = filtered.filter((result) => {
        const yearValue = result.year ? String(result.year) : null;
        if (yearValue) return yearValue === selectedYear;
        const dateStr = result.test?.date || result.testDate || result.createdAt;
        if (!dateStr) return false;
        const resultYear = new Date(dateStr).getFullYear();
        return resultYear.toString() === selectedYear;
      });
    }

    // Filter by assessment type
    if (selectedAssessmentType !== "all") {
      filtered = filtered.filter((result) => {
        const type = result.examType || result.test?.title || result.testTitle;
        return type === selectedAssessmentType;
      });
    }

    // Filter by term
    if (selectedTerm !== "all") {
      filtered = filtered.filter((result) => {
        const term = (result as any).term || result.test?.term;
        return term === selectedTerm;
      });
    }

    return filtered;
  }, [testResults, selectedYear, selectedAssessmentType, selectedTerm]);

  const handleExportPDF = async () => {
    if (!token) return;
    if (selectedYear === "all" || selectedTerm === "all") {
      alert("Please select a specific year and term before exporting.");
      return;
    }
    try {
      const params = new URLSearchParams({
        studentId: student.id,
        classId: student.classId || "",
        term: selectedTerm,
        year: selectedYear
      });
      const response = await axios.get(`${getApiUrl("export/pdf/student")}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${student.name}_Report.pdf`);
      document.body.appendChild(link);
      link.click();
    } catch (err) { console.error(err); }
  };

  const handleExportWord = async () => {
    if (!token) return;
    if (selectedYear === "all" || selectedTerm === "all") {
      alert("Please select a specific year and term before exporting.");
      return;
    }
    try {
      const params = new URLSearchParams({
        studentId: student.id,
        classId: student.classId || "",
        term: selectedTerm,
        year: selectedYear
      });
      const response = await axios.get(`${getApiUrl("export/word/student")}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${student.name}_Report.docx`);
      document.body.appendChild(link);
      link.click();
    } catch (err) { console.error(err); }
  };

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const rows = [
      ["MILESTONE SCHOOL - INDIVIDUAL PERFORMANCE REPORT"],
      [`Student: ${student.name.toUpperCase()}`],
      [`Class: ${student.className || 'N/A'}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [""],
      ["TERM/YEAR", "SUBJECT", "SCORE (%)", "GRADE", "STATUS"]
    ];

    groupedResults.forEach(([termYear, results]) => {
      results.forEach((r: any) => {
        const score = r.score;
        const grade = score >= 80 ? "A" : score >= 70 ? "B" : score >= 60 ? "C" : score >= 50 ? "D" : "F";
        rows.push([
          termYear,
          (r.test?.subject?.name || r.subjectName || "N/A").toUpperCase(),
          score.toString(),
          grade,
          score >= 50 ? "PASSED" : "FAILED"
        ]);
      });
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    if (ws["!ref"]) {
      const range = XLSX.utils.decode_range(ws["!ref"]);
      const lastCol = range.e.c;
      const lastRow = range.e.r;
      ws["!cols"] = [{ wch: 20 }, { wch: 30 }, { wch: 12 }, { wch: 10 }, { wch: 12 }];

      if (!ws["!merges"]) ws["!merges"] = [];
      ws["!merges"].push({ s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } });
      ws["!merges"].push({ s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } });
      ws["!merges"].push({ s: { r: 2, c: 0 }, e: { r: 2, c: lastCol } });

      for (let r = 0; r <= lastRow; r++) {
        for (let c = 0; c <= lastCol; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (!ws[addr]) continue;
          ws[addr].s = {
            font: { name: "Arial", sz: 10 },
            alignment: { vertical: "center", horizontal: c === 0 ? "left" : "center" },
            border: {
              top: { style: "thin", color: { rgb: "E2E8F0" } },
              bottom: { style: "thin", color: { rgb: "E2E8F0" } },
              left: { style: "thin", color: { rgb: "E2E8F0" } },
              right: { style: "thin", color: { rgb: "E2E8F0" } }
            }
          };
          if (r === 0) {
            ws[addr].s = {
              font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "1E40AF" } },
              alignment: { horizontal: "center" }
            };
          } else if (r === 5) {
            ws[addr].s.font.bold = true;
            ws[addr].s.fill = { fgColor: { rgb: "F1F5F9" } };
          }
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, "Performance");
    XLSX.writeFile(wb, `${student.name}_Performance.xlsx`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "present":
        return { bg: "#d1fae5", text: "#065f46" };
      case "absent":
        return { bg: "#fee2e2", text: "#991b1b" };
      case "absent_with_permission":
        return { bg: "#fef3c7", text: "#92400e" };
      default:
        return { bg: "#f3f4f6", text: "#374151" };
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Calculate stats based on filtered data (all hooks must be before early return)
  const attendanceStats = useMemo(() => {
    const total = filteredAttendance.length;
    const present = filteredAttendance.filter((a) => a.status === "present").length;
    const absent = filteredAttendance.filter((a) => a.status === "absent").length;
    const absentWithPermission = filteredAttendance.filter(
      (a) => a.status === "absent_with_permission"
    ).length;
    const attendanceRate = total > 0 ? ((present / total) * 100).toFixed(1) : "0";
    return { total, present, absent, absentWithPermission, attendanceRate };
  }, [filteredAttendance]);

  const averageScore = useMemo(() => {
    if (filteredTestResults.length === 0) return "0";
    const sum = filteredTestResults.reduce((acc, result) => acc + result.score, 0);
    return (sum / filteredTestResults.length).toFixed(1);
  }, [filteredTestResults]);

  const groupedResults = useMemo(() => {
    const grouped: Record<string, TestResult[]> = {};
    filteredTestResults.forEach((result) => {
      const termValue = (result as any).term || result.test?.term;
        const yearValue = (result as any).year;
      const dateStr = result.test?.date || result.testDate || result.createdAt;
      let key = "";
      if (termValue && yearValue) {
        key = `${yearValue} - ${termValue}`;
      } else if (dateStr) {
        const { term, academicYear } = getTermFromDate(dateStr);
        key = `${academicYear} - ${term}`;
      }
      if (!key) return;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(result);
    });
    Object.keys(grouped).forEach((k) => {
      grouped[k].sort((a, b) => {
        const ad = new Date(a.test?.date || a.testDate || a.createdAt || 0).getTime();
        const bd = new Date(b.test?.date || b.testDate || b.createdAt || 0).getTime();
        return bd - ad;
      });
    });
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredTestResults]);

  return (
    <div className="spm-modal-overlay" onClick={onClose}>
      <div className="spm-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="bg-white px-8 py-6 flex items-center justify-between border-b border-slate-50 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-2 h-full bg-teal-500"></div>
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center text-teal-600 border border-teal-200 shadow-sm transition-transform hover:scale-105">
              <User size={32} />
            </div>
            <div>
              <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tight">{student.name}</h3>
              <div className="flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1.5 text-[10px] font-black text-teal-600 bg-teal-50 px-2 py-1 rounded-md uppercase tracking-wider">
                  <GraduationCap size={12} /> {student.className || "N/A"}
                </span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{student.email}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 mr-4 bg-slate-50 p-1.5 rounded-xl border border-slate-100">
              <button onClick={handleExportExcel} className="p-2 hover:bg-green-600 hover:text-white rounded-lg transition-all text-green-600" title="Export Excel"><FileSpreadsheet size={16} /></button>
              <button onClick={handleExportPDF} className="p-2 hover:bg-rose-600 hover:text-white rounded-lg transition-all text-rose-600" title="Export PDF"><Printer size={16} /></button>
              <button onClick={handleExportWord} className="p-2 hover:bg-blue-600 hover:text-white rounded-lg transition-all text-blue-600" title="Export Word"><FileType size={16} /></button>
            </div>
            <button className="w-10 h-10 rounded-xl bg-slate-100 text-slate-600 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>


        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-8 bg-slate-50/50 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-400">
              <Filter size={18} />
            </div>
            <div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] leading-none">View</p>
              <p className="text-xs font-black text-slate-700 uppercase mt-1">Filters</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">Year</label>
            <select
              className="utopia-input h-12 text-xs font-black uppercase"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="all">All Academic Years</option>
              {years.map((y) => (
                <option key={y} value={y.toString()}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">Term</label>
            <select
              className="utopia-input h-12 text-xs font-black uppercase"
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
            >
              <option value="all">All Terms</option>
              {TERM_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">Assessment Type</label>
            <select
              className="utopia-input h-12 text-xs font-black uppercase"
              value={selectedAssessmentType}
              onChange={(e) => setSelectedAssessmentType(e.target.value)}
            >
              <option value="all">All Assessments</option>
              {assessmentTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-end">
            <div className="text-right">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] leading-none">Overall</p>
              <p className="text-lg font-black text-teal-600 uppercase mt-1 italic">{averageScore}%</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 bg-white">
          <button
            onClick={() => setActiveTab("attendance")}
            className={`flex-1 py-4 flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest transition-all border-b-2 ${activeTab === "attendance" ? "border-teal-500 text-teal-600 bg-teal-50/10" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            <Calendar size={16} /> Attendance Snapshot ({filteredAttendance.length})
          </button>
          <button
            onClick={() => setActiveTab("results")}
            className={`flex-1 py-4 flex items-center justify-center gap-2 text-[11px] font-black uppercase tracking-widest transition-all border-b-2 ${activeTab === "results" ? "border-teal-500 text-teal-600 bg-teal-50/10" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            <BookOpen size={16} /> Academic Performance ({filteredTestResults.length})
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <p className="spm-loading">Loading...</p>
        ) : (
          <>
            {activeTab === "attendance" && (
              <div className="spm-attendance-section">
                {filteredAttendance.length === 0 ? (
                  <p className="spm-empty">No attendance records found for the selected filters.</p>
                ) : (
                  <div className="spm-table-wrap">
                    <table className="spm-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Class</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAttendance.map((record) => {
                          const statusColors = getStatusColor(record.status);
                          return (
                            <tr key={record.id}>
                              <td>{formatDate(record.date)}</td>
                              <td>
                                <span
                                  className="spm-badge"
                                  style={{ backgroundColor: statusColors.bg, color: statusColors.text }}
                                >
                                  {record.status.replace("_", " ")}
                                </span>
                              </td>
                              <td>{record.className || student.className || "N/A"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === "results" && (
              <div className="spm-results-section">
                {filteredTestResults.length === 0 ? (
                  <div className="p-20 text-center flex flex-col items-center gap-4 opacity-20">
                    <BookOpen size={64} className="text-slate-400" />
                    <p className="font-black uppercase tracking-[0.2em] text-xs">No analysis available</p>
                  </div>
                ) : (
                  <div className="p-8 space-y-10">
                    {groupedResults.map(([termYear, results]: [string, TestResult[]]) => {
                      const termAverage =
                        results.reduce((sum: number, r: TestResult) => sum + r.score, 0) / results.length;
                      return (
                        <div key={termYear} className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/50 overflow-hidden">
                          <div className="px-8 py-5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                            <div>
                              <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">{termYear}</h5>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-sm font-black text-slate-700 uppercase tracking-tight">Term Summary</span>
                                <span className="text-[10px] font-bold text-slate-400">• {results.length} Assessments</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-[9px] font-black text-slate-400 uppercase">Term Average</p>
                              <p className={`text-xl font-black italic ${termAverage >= 50 ? 'text-teal-600' : 'text-rose-500'}`}>{termAverage.toFixed(1)}%</p>
                            </div>
                          </div>

                          <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                              <thead>
                                <tr className="bg-slate-50/30">
                                  <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Assessment Title</th>
                                  <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Subject</th>
                                  <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Score</th>
                                  <th className="px-8 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Performance</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50">
                                {results.map((result: TestResult) => (
                                  <tr key={result.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-8 py-4 text-xs font-black text-slate-700 uppercase tracking-tight">{result.test?.title || result.testTitle}</td>
                                    <td className="px-8 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">{result.test?.subject?.name || result.subjectName}</td>
                                    <td className="px-8 py-4 text-center">
                                      <span className={`text-xs font-black px-3 py-1 rounded-lg ${result.score >= 80 ? 'bg-teal-500 text-white shadow-lg shadow-teal-100' : result.score < 50 ? 'bg-rose-50 text-rose-500' : 'bg-slate-100 text-slate-600'}`}>
                                        {result.score}%
                                      </span>
                                    </td>
                                    <td className="px-8 py-4 text-right">
                                      <div className="w-24 bg-slate-100 h-1.5 rounded-full inline-block overflow-hidden">
                                        <div className={`h-full rounded-full ${result.score >= 80 ? 'bg-teal-500' : result.score < 50 ? 'bg-rose-500' : 'bg-blue-500'}`} style={{ width: `${result.score}%` }}></div>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StudentPerformanceModal;
